import { useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { MapPin, Phone, Mail, Clock, Send, Facebook, Twitter, Instagram, Linkedin } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';

const ContactUsPage = () => {
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: ''
  });

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    // Simulate form submission
    setTimeout(() => {
      toast.success('Message sent successfully! We\'ll get back to you soon.');
      setFormData({
        name: '',
        email: '',
        phone: '',
        subject: '',
        message: ''
      });
      setLoading(false);
    }, 1500);
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Hero Section */}
      <section className="relative py-20 px-6 md:px-12 lg:px-20">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-primary/20 via-background to-background"></div>
        <div className="relative z-10 container mx-auto max-w-6xl">
          <div className="flex justify-end mb-4">
            <Link to="/">
              <Button variant="outline" className="border-primary text-primary hover:bg-primary hover:text-white" data-testid="back-home-btn">
                Back to Home
              </Button>
            </Link>
          </div>
          <div className="text-center">
            <h1 className="text-5xl md:text-6xl font-heading font-black uppercase tracking-tight mb-6" data-testid="contact-title">
              Get In Touch
            </h1>
            <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
              Have questions? We'd love to hear from you. Send us a message and we'll respond as soon as possible.
            </p>
          </div>
        </div>
      </section>

      {/* Contact Info & Form */}
      <section className="py-12 px-6 md:px-12 lg:px-20">
        <div className="container mx-auto max-w-6xl">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Contact Information */}
            <div className="lg:col-span-1 space-y-6">
              <Card className="bg-card/50 border-white/10">
                <CardContent className="pt-6">
                  <div className="flex items-start space-x-4 mb-6">
                    <div className="bg-primary/20 p-3 rounded-lg">
                      <MapPin className="w-6 h-6 text-primary" />
                    </div>
                    <div>
                      <h3 className="font-heading font-bold uppercase mb-1">Location</h3>
                      <p className="text-sm text-muted-foreground">
                        Wholesome Fitness Center<br />
                        4 Owode Oyelaja, Tipper Garage, Ketu<br />
                        Lagos, Nigeria.
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start space-x-4 mb-6">
                    <div className="bg-primary/20 p-3 rounded-lg">
                      <Phone className="w-6 h-6 text-primary" />
                    </div>
                    <div>
                      <h3 className="font-heading font-bold uppercase mb-1">Phone / WhatsApp</h3>
                      <p className="text-sm text-muted-foreground">
                        +234 814 231 3787<br />
                        +234 803 346 9844
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start space-x-4 mb-6">
                    <div className="bg-primary/20 p-3 rounded-lg">
                      <Mail className="w-6 h-6 text-primary" />
                    </div>
                    <div>
                      <h3 className="font-heading font-bold uppercase mb-1">Email</h3>
                      <p className="text-sm text-muted-foreground">
                        wholesomefitnesscentre@gmail.com
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start space-x-4">
                    <div className="bg-primary/20 p-3 rounded-lg">
                      <Clock className="w-6 h-6 text-primary" />
                    </div>
                    <div>
                      <h3 className="font-heading font-bold uppercase mb-1">Operating Hours</h3>
                      <p className="text-sm text-muted-foreground">
                        Monday - Friday: 5:00 AM - 11:00 PM<br />
                        Saturday: 6:00 AM - 10:00 PM<br />
                        Sunday: 7:00 AM - 9:00 PM
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Social Media & WhatsApp */}
              <Card className="bg-card/50 border-white/10">
                <CardHeader>
                  <CardTitle className="text-lg">Connect With Us</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {/* WhatsApp */}
                    <a 
                      href="https://wa.me/2348142313787" 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="flex items-center gap-3 p-3 bg-green-500/20 rounded-lg hover:bg-green-500/30 transition-colors"
                    >
                      <div className="bg-green-500 p-2 rounded-full">
                        <Phone className="w-5 h-5 text-white" />
                      </div>
                      <div>
                        <p className="font-semibold text-green-500">WhatsApp</p>
                        <p className="text-sm text-muted-foreground">Chat with us</p>
                      </div>
                    </a>
                    
                    {/* Instagram */}
                    <a 
                      href="https://instagram.com/wholesomefitness_center" 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="flex items-center gap-3 p-3 bg-pink-500/20 rounded-lg hover:bg-pink-500/30 transition-colors"
                    >
                      <div className="bg-gradient-to-br from-purple-500 via-pink-500 to-orange-500 p-2 rounded-full">
                        <Instagram className="w-5 h-5 text-white" />
                      </div>
                      <div>
                        <p className="font-semibold text-pink-500">Instagram</p>
                        <p className="text-sm text-muted-foreground">@wholesomefitness_center</p>
                      </div>
                    </a>
                  </div>
                  
                  {/* Other Social Links */}
                  <div className="flex space-x-4 mt-4 pt-4 border-t border-white/10">
                    <a href="#" className="bg-primary/20 p-3 rounded-lg hover:bg-primary/30 transition-colors">
                      <Facebook className="w-5 h-5 text-primary" />
                    </a>
                    <a href="#" className="bg-primary/20 p-3 rounded-lg hover:bg-primary/30 transition-colors">
                      <Twitter className="w-5 h-5 text-primary" />
                    </a>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Contact Form */}
            <div className="lg:col-span-2">
              <Card className="bg-card/50 border-white/10">
                <CardHeader>
                  <CardTitle className="text-2xl font-heading uppercase">Send Us a Message</CardTitle>
                </CardHeader>
                <CardContent>
                  <form onSubmit={handleSubmit} className="space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="name">Full Name *</Label>
                        <Input
                          id="name"
                          name="name"
                          value={formData.name}
                          onChange={handleInputChange}
                          required
                          className="bg-background/50 border-white/10 h-12"
                          data-testid="contact-name-input"
                        />
                      </div>

                      <div>
                        <Label htmlFor="email">Email Address *</Label>
                        <Input
                          id="email"
                          name="email"
                          type="email"
                          value={formData.email}
                          onChange={handleInputChange}
                          required
                          className="bg-background/50 border-white/10 h-12"
                          data-testid="contact-email-input"
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="phone">Phone Number</Label>
                        <Input
                          id="phone"
                          name="phone"
                          type="tel"
                          value={formData.phone}
                          onChange={handleInputChange}
                          className="bg-background/50 border-white/10 h-12"
                        />
                      </div>

                      <div>
                        <Label htmlFor="subject">Subject *</Label>
                        <Input
                          id="subject"
                          name="subject"
                          value={formData.subject}
                          onChange={handleInputChange}
                          required
                          className="bg-background/50 border-white/10 h-12"
                          data-testid="contact-subject-input"
                        />
                      </div>
                    </div>

                    <div>
                      <Label htmlFor="message">Message *</Label>
                      <Textarea
                        id="message"
                        name="message"
                        value={formData.message}
                        onChange={handleInputChange}
                        required
                        rows={6}
                        className="bg-background/50 border-white/10"
                        placeholder="Tell us how we can help you..."
                        data-testid="contact-message-input"
                      />
                    </div>

                    <Button
                      type="submit"
                      disabled={loading}
                      className="w-full bg-primary hover:bg-primary/90 h-12 text-lg font-bold uppercase"
                      data-testid="contact-submit-btn"
                    >
                      {loading ? 'Sending...' : (
                        <>
                          <Send className="w-5 h-5 mr-2" />
                          Send Message
                        </>
                      )}
                    </Button>
                  </form>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Map Section */}
      <section className="py-12 px-6 md:px-12 lg:px-20">
        <div className="container mx-auto max-w-6xl">
          <Card className="bg-card/50 border-white/10 overflow-hidden">
            <div className="h-96">
              <iframe
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3963.5!2d3.3897!3d6.6018!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x103b922e09a8e00d%3A0x7e9eb13c34e0c5a0!2sKetu%2C%20Lagos!5e0!3m2!1sen!2sng!4v1707400000000!5m2!1sen!2sng"
                width="100%"
                height="100%"
                style={{ border: 0 }}
                allowFullScreen=""
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title="Wholesome Fitness Center - Ketu, Lagos"
                data-testid="google-map"
              ></iframe>
            </div>
          </Card>
          <p className="text-center text-muted-foreground mt-4">
            📍 4 Owode Oyelaja, Tipper Garage, Ketu, Lagos, Nigeria.
          </p>
        </div>
      </section>

      {/* Quick Links */}
      <section className="py-12 px-6 md:px-12 lg:px-20 bg-gradient-to-b from-transparent via-primary/5 to-transparent">
        <div className="container mx-auto max-w-6xl text-center">
          <h2 className="text-3xl font-heading font-black uppercase mb-8">Quick Links</h2>
          <div className="flex flex-wrap gap-4 justify-center">
            <Link to="/plans">
              <Button variant="outline" className="border-primary text-primary hover:bg-primary hover:text-white">
                Membership Plans
              </Button>
            </Link>
            <Link to="/store">
              <Button variant="outline" className="border-secondary text-secondary hover:bg-secondary hover:text-black">
                Gym Store
              </Button>
            </Link>
            <Link to="/register">
              <Button variant="outline" className="border-primary text-primary hover:bg-primary hover:text-white">
                Register Now
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ContactUsPage;